import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PinInput } from "@/components/PinInput";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { bioSupported, registerApprovalBio, removeApprovalBio, approvalBioHere, bioCancelled } from "@/lib/biometric";

export const ApprovalBioButton = () => {
  const [open, setOpen] = useState(false);
  const [supported, setSupported] = useState(null);
  const [creds, setCreds] = useState([]);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const localId = approvalBioHere();
  const here = creds.some((c) => c.credential_id === localId);

  const load = () => api.get("/webauthn/approval/credentials").then((r) => setCreds(r.data)).catch(() => {});
  useEffect(() => {
    bioSupported().then(setSupported);
    load();
  }, []);

  const enable = async (value) => {
    setBusy(true);
    setErr("");
    try {
      await registerApprovalBio(value);
      toast.success("Persetujuan sidik jari aktif di perangkat ini");
      setPin("");
      await load();
    } catch (e) {
      setPin("");
      if (bioCancelled(e)) setErr("Pendaftaran sidik jari dibatalkan");
      else setErr(e?.response ? errMsg(e) : "Perangkat menolak pendaftaran sidik jari");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await removeApprovalBio(id);
      toast.success("Sidik jari persetujuan dihapus");
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <>
      <button onClick={() => { setErr(""); setPin(""); setOpen(true); }} data-testid="approval-bio-open" aria-label="Persetujuan sidik jari"
        className={`p-2 rounded-full transition-colors ${here ? "text-emerald-600 bg-emerald-50 hover:bg-emerald-100" : "text-slate-500 hover:bg-slate-100"}`}>
        <Fingerprint className="h-5 w-5" />
      </button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="max-w-sm" data-testid="approval-bio-dialog">
          <DialogHeader>
            <DialogTitle className="text-center">Persetujuan dengan Sidik Jari</DialogTitle>
            <DialogDescription className="text-center">Setujui aksi penting dengan sidik jari di perangkat ini, tanpa mengetik PIN Super Admin. PIN tetap bisa dipakai.</DialogDescription>
          </DialogHeader>
          {here && <p className="text-sm text-center font-semibold text-emerald-700" data-testid="approval-bio-status-on">Aktif di perangkat ini</p>}
          {supported === false && !here && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2.5" data-testid="approval-bio-unsupported">Perangkat atau browser ini belum mendukung sidik jari.</p>}
          {supported && !here && (
            <>
              <p className="text-xs text-center text-slate-600">Masukkan PIN Super Admin untuk mengizinkan perangkat ini, lalu tempelkan jari pada sensor.</p>
              <PinInput value={pin} onChange={setPin} onComplete={enable} disabled={busy} testid="approval-bio-pin-input" />
              <button onClick={() => enable(pin)} disabled={busy || pin.length !== 6} data-testid="approval-bio-enable"
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold py-3 rounded-xl">{busy ? "Memproses..." : "Aktifkan"}</button>
            </>
          )}
          {err && <p className="text-sm text-center text-red-600 font-medium" data-testid="approval-bio-error">{err}</p>}
          {creds.length > 0 && (
            <div className="space-y-2" data-testid="approval-bio-list">
              {creds.map((c) => (
                <div key={c.credential_id} className="flex items-center justify-between text-sm border border-slate-100 rounded-lg px-3 py-2">
                  <div>
                    <div className="font-medium text-slate-800">{c.device}{c.credential_id === localId && " (perangkat ini)"}</div>
                    <div className="text-[11px] text-slate-500">Didaftarkan {fmtDate(c.created_at)}</div>
                  </div>
                  <button onClick={() => remove(c.credential_id)} data-testid={`approval-bio-remove-${c.credential_id.slice(0, 8)}`} className="p-2 text-slate-400 hover:text-red-600" aria-label="Hapus"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
